import { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { toggleDsPick } from 'redux/mode/actions';
import { DesignSystemToken } from 'designSystem/types';
import { formatTokenValue } from '../tokenUtil';
import * as Base from '../styles';
import * as S from './styles';

interface ElevationScaleProps {
	tokens: DesignSystemToken[];
}

const isShadowToken = (token: DesignSystemToken) => token.value.kind === 'shadow';

const getShadowIntensity = (token: DesignSystemToken): number => {
	if (!isShadowToken(token)) return 0;
	const v = token.value as { blur: number; spread?: number; offsetY: number };
	return v.blur + (v.spread ?? 0) + Math.abs(v.offsetY);
};

const buildShadowCss = (token: DesignSystemToken): string => {
	if (!isShadowToken(token)) return token.value.original;
	const v = token.value as {
		offsetX: number;
		offsetY: number;
		blur: number;
		spread?: number;
		color: { hex: string };
	};
	return `${v.offsetX}px ${v.offsetY}px ${v.blur}px ${v.spread ?? 0}px ${v.color.hex}`;
};

const ElevationScale = ({ tokens }: ElevationScaleProps) => {
	const dispatch = useDispatch();
	const selectedPicks = useSelector((r: RootReducer) => r.mode.selectedDsPicks);

	const sorted = useMemo(
		() => [...tokens].sort((a, b) => getShadowIntensity(a) - getShadowIntensity(b)),
		[tokens]
	);

	const isSelected = (token: DesignSystemToken) =>
		selectedPicks.some((p) => p.kind === 'token' && p.cssVariable === token.cssVariable);

	return (
		<S.Base>
			{sorted.map((token) => (
				<Base.ScaleItem
					key={token.id}
					type="button"
					$selected={isSelected(token)}
					onClick={() =>
						dispatch(toggleDsPick({ kind: 'token', cssVariable: token.cssVariable }))
					}
				>
					<Base.ScaleTooltip>{token.cssVariable}<br />{formatTokenValue(token.value)}</Base.ScaleTooltip>
					<S.Card $shadow={buildShadowCss(token)} />
					<Base.ScaleLabel>{token.name}</Base.ScaleLabel>
				</Base.ScaleItem>
			))}
		</S.Base>
	);
};

export default ElevationScale;
